import { gql } from '@apollo/client';
import { FunctionWithFragments } from '@modernloop/shared/components';
import { v4 as uuid } from 'uuid';

import {
  GetProvideAvailabilitySettings_JobStageFragment,
  GetProvideAvailabilitySettings_JobStageSettingsV2Fragment,
} from 'src/generated/mloop-graphql';

import cloneInterviewPlan from 'src/entities/InterviewPlan/cloneInterviewPlan';
import getSaveInterviewPlanInput from 'src/entities/InterviewPlan/getSaveInterviewPlanInput';

import {
  DEFAULT_MINIMUM_TIME_BLOCK_MINUTES,
  DEFAULT_MINUTES_PER_DAY,
  DEFAULT_NUMBER_OF_DAYS,
} from 'src/store/slices/candidate-availability-options';

import { getBasicAvailabilityOptionsConfigV2 } from './getBasicAvailabilityOptionsConfig';

type Fragment = {
  jobStage: GetProvideAvailabilitySettings_JobStageFragment | null | undefined;
  jobStageSettingsV2: GetProvideAvailabilitySettings_JobStageSettingsV2Fragment | null | undefined;
};

type Props = {
  collaborationLinkEnabled: boolean;
};

export const getProvideAvailabilitySettings: FunctionWithFragments<
  Fragment,
  Props,
  {
    numberOfDays: number;
    minutesPerDays: number;
    minimumTimeBlockMinutes: number;
  }
> = ({ jobStage, jobStageSettingsV2 }, { collaborationLinkEnabled }) => {
  if (!jobStage) {
    return {
      minimumTimeBlockMinutes: DEFAULT_MINIMUM_TIME_BLOCK_MINUTES,
      numberOfDays: DEFAULT_NUMBER_OF_DAYS,
      minutesPerDays: DEFAULT_MINUTES_PER_DAY,
    };
  }
  const newCustomJobStageId = uuid();

  const interviewPlanClone = cloneInterviewPlan({ interviewPlan: jobStage }, { customJobStageId: newCustomJobStageId });

  const interviewPlanInput = getSaveInterviewPlanInput(
    { interviewPlan: interviewPlanClone },
    { finalJobStageId: newCustomJobStageId, collaborationLinkEnabled }
  );

  return getBasicAvailabilityOptionsConfigV2(
    {
      settings: jobStageSettingsV2?.candidateAvailabilitySettingsV2 ?? undefined,
    },
    {
      groups: interviewPlanInput.groups || [],
    }
  );
};

getProvideAvailabilitySettings.fragments = {
  jobStage: gql`
    ${cloneInterviewPlan.fragments.interviewPlan}
    ${getSaveInterviewPlanInput.fragments.interviewPlan}
    fragment getProvideAvailabilitySettings_JobStage on JobStage {
      id
      ...cloneInterviewPlan_interviewPlan
      ...getSaveInterviewPlanInput_interviewPlan
    }
  `,
  jobStageSettingsV2: gql`
    ${getBasicAvailabilityOptionsConfigV2.fragments.settings}
    fragment getProvideAvailabilitySettings_JobStageSettingsV2 on JobStageSettingsV2 {
      id
      candidateAvailabilitySettingsV2 {
        ...getBasicAvailabilityOptionsConfigV2_settings
      }
    }
  `,
};
